import { ToastService } from '@ih/app/client/shared/services';

/**
 * A row-insert `Error` carrying the extra `errorColumn`/`errorDetail`
 * fields `submitFormAnswers` attaches via `Object.assign` (see
 * `form.state.ts`/`share-form.state.ts`'s `SubmitAnswersAction` handlers).
 * Replaces the untyped `(error as any)?.errorColumn` cast previously
 * duplicated in both states' `AddRowError`/`AddShareRowError` handlers.
 */
export interface FormInsertError extends Error {
  errorColumn?: string;
  errorDetail?: string;
}

/**
 * Friendly messages for the error *codes* returned by the `submitFormAnswers`
 * resolver. The resolver surfaces only the `SubmitFormAnswersError.code`
 * (not its descriptive message), so the client maps the code here.
 */
const FRIENDLY_BY_CODE: Record<string, string> = {
  'unsafe-filename':
    'One of your attached files has a name with unsupported characters. Please rename the file using letters, numbers, spaces, and only these symbols: . - _ ( ) + : (keep the file extension), then attach it again.',
  'template-unresolvable':
    "We couldn't work out where to store one of your uploaded files because a field it depends on is empty. Please complete the form and try again.",
  'pending-url-mismatch':
    "We couldn't locate one of your uploaded files. Please remove and re-attach the file, then submit again.",
  forbidden: "You don't have permission to submit this form.",
  'form-not-found':
    'This form could no longer be found. It may have been moved or deleted.',
  'submit-form-answers-failed': 'Failed to submit form. Please try again.',
};

const PRIMARY_KEY_COLUMNS = new Set(['id']);

/** Codes classified by {@link classifyInsertError} (`data.repository.ts`) and threaded through `submitFormAnswers` (REQ-4/REQ-5). */
const INSERT_ERROR_CODES = new Set([
  'row-required-field',
  'row-unique-constraint',
  'row-foreign-key',
  'row-type-mismatch',
  'row-insert-failed',
]);

function humaniseColumn(name: string): string {
  return name
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function friendlyInsertError(code: string, column?: string): string {
  switch (code) {
    case 'row-required-field': {
      if (!column || PRIMARY_KEY_COLUMNS.has(column)) {
        return 'Form failed: Primary Key required';
      }
      return `Form failed: "${humaniseColumn(column)}" is required`;
    }
    case 'row-unique-constraint':
      return column
        ? `Form failed: Unique constraint on column ${column}`
        : 'Form failed: Unique constraint violation';
    case 'row-foreign-key':
      return column
        ? `Form failed: Referenced record not found for column ${column}`
        : 'Form failed: Referenced record not found';
    case 'row-type-mismatch':
      return "Form failed: Value doesn't match column type";
    case 'row-insert-failed':
    default:
      return 'Form failed: Row could not be inserted';
  }
}

/**
 * Builds a multi-line, developer-facing description of a form-submission
 * insert failure (REQ-6) — the counterpart to {@link toFriendlyFormError}'s
 * short, human-facing message. Suitable for copying into a bug report.
 */
export function buildFormErrorDetail(input: {
  code?: string;
  column?: string;
  detail?: string;
  formId?: string;
}): string {
  const lines = [
    'Form submission failed',
    `Reason: ${input.code ?? 'unknown'}`,
    `Column: ${input.column ?? 'unknown'}`,
    `Detail: ${input.detail ?? 'none'}`,
    `Form ID: ${input.formId ?? 'unknown'}`,
    `Time: ${new Date().toISOString()}`,
  ];
  return lines.join('\n');
}

/**
 * Translates a raw form-submission error (a `submitFormAnswers` error code, or
 * a raw GraphQL / Prisma message from the legacy insert path) into something a
 * form filler can act on. Falls back to a cleaned-up version of the raw
 * message when no pattern matches.
 */
export function toFriendlyFormError(
  raw: string | null | undefined,
  column?: string,
): string {
  const message = (raw ?? '').trim();
  if (!message) return 'Failed to submit form. Please try again.';

  if (INSERT_ERROR_CODES.has(message)) {
    return friendlyInsertError(message, column);
  }

  // Known resolver error codes take precedence (exact match).
  if (Object.prototype.hasOwnProperty.call(FRIENDLY_BY_CODE, message)) {
    return FRIENDLY_BY_CODE[message];
  }

  // Prisma NOT NULL: `null value in column "X" of relation "Y" violates not-null constraint`
  const notNull = message.match(
    /null value in column "([^"]+)" of relation "([^"]+)"/i,
  );
  if (notNull) {
    const column = humaniseColumn(notNull[1]);
    const table = humaniseColumn(notNull[2]);
    return `"${column}" (in the "${table}" table) is required by the database but was left empty. Please fill it in and resubmit. (If this field isn't visible on the form, the form's configuration needs to be updated.)`;
  }

  // Prisma unique constraint: `Unique constraint failed on the fields: (\`X\`)`
  const unique = message.match(
    /Unique constraint failed on the fields?: \(`?([^`)]+)`?\)/i,
  );
  if (unique) {
    const column = humaniseColumn(unique[1]);
    return `"${column}" must be unique. A row with this value already exists.`;
  }

  // Prisma foreign key: `Foreign key constraint failed on the field: \`X\``
  const fk = message.match(
    /Foreign key constraint failed on the field: `?([^`]+)`?/i,
  );
  if (fk) {
    const column = humaniseColumn(fk[1]);
    return `"${column}" references a record that no longer exists. Please re-select a value.`;
  }

  // Strip Prisma noise from any other message.
  const stripped = message
    .replace(/Invalid `prisma\.[^`]+` invocation:?/i, '')
    .replace(/PrismaClient\w*Error:?/i, '')
    .replace(/Raw query failed\.?\s*Code:\s*`[^`]*`\.?/i, '')
    .replace(/Message:\s*`?/i, '')
    .replace(/`$/, '')
    .trim();
  return 'Failed to submit form: ' + (stripped || message);
}

/**
 * Shapes a row-insert failure into the friendly toast message + developer
 * detail and shows it via `ToastService.showErrorWithCopy`. Shared by
 * `FormState.addRowError` and `ShareFormState.addShareRowError`, which
 * previously duplicated this logic (including the untyped
 * `(error as any)?.errorColumn` cast this now replaces with
 * {@link FormInsertError}).
 */
export function dispatchFormInsertErrorToast(
  toastService: ToastService,
  error: FormInsertError | undefined,
  formId: string | undefined,
): void {
  const friendly = toFriendlyFormError(
    error?.message ?? '',
    error?.errorColumn,
  );
  const detail = buildFormErrorDetail({
    code: error?.message,
    column: error?.errorColumn,
    detail: error?.errorDetail,
    formId,
  });
  toastService.showErrorWithCopy(friendly, detail);
}
